import * as Sentry from "@sentry/vue"

/**
 * Sets user context in Sentry for error tracking and debugging
 * @param {Object} userData - User data object
 * @param {string} userData.id - User ID
 * @param {string} userData.email - User email
 * @param {string} userData.role - User role (student/teacher)
 * @param {string} userData.first_name - User first name
 * @param {string} userData.last_name - User last name
 * @param {string} userData.phone_number - User phone number
 * @param {boolean} userData.registration_completed - Whether registration is completed
 * @param {string} userData.active_until - User subscription active until date
 */
export function setSentryUserContext(userData) {
  if (!userData) {
    clearSentryUserContext()
    return
  }

  const {
    id,
    email,
    role,
    first_name,
    last_name,
    phone_number,
    registration_completed,
    active_until,
  } = userData

  Sentry.setUser({
    id: id?.toString(),
    email: email,
    username: first_name && last_name ? `${first_name} ${last_name}` : email,
    // Additional user context
    role: role,
    firstName: first_name,
    lastName: last_name,
    phoneNumber: phone_number,
    registrationCompleted: registration_completed,
    activeUntil: active_until,
  })

  // Set additional context tags for better filtering in Sentry
  Sentry.setContext("user_profile", {
    role: role,
    registrationCompleted: registration_completed,
    hasActiveSubscription: active_until ? new Date(active_until) > new Date() : false,
  })

  // Set environment context
  Sentry.setTag("user_role", role)
  Sentry.setTag("registration_status", registration_completed ? "completed" : "incomplete")
}

/**
 * Clears user context from Sentry (useful for logout)
 */
export function clearSentryUserContext() {
  Sentry.setUser(null)
  Sentry.setContext("user_profile", null)
  Sentry.setTag("user_role", null)
  Sentry.setTag("registration_status", null)
}

/**
 * Sets additional context for specific features or pages
 * @param {string} feature - Feature name (e.g., 'lesson_booking', 'profile_edit')
 * @param {Object} context - Additional context data
 */
export function setSentryFeatureContext(feature, context = {}) {
  Sentry.setContext(`feature_${feature}`, {
    ...context,
    timestamp: new Date().toISOString(),
  })
  Sentry.setTag("current_feature", feature)
}

/**
 * Clears feature context
 */
export function clearSentryFeatureContext() {
  Sentry.setTag("current_feature", null)
}
